import {ALIGN_H} from '../../../../shared/model/surveys/align-h.model';
import {ANIMATION_MODE} from '../../../../shared/model/surveys/animation.mode.model';
import {FormConfiguration} from './form.configuration.model';
import {ThemeConfiguration} from './theme.configuration.model';

/**
 * Valeurs éditées dans la vue Style de l'éditeur d'enquête.
 *
 * Le brouillon range l'habillage dans `information.theme.themeConfiguration` et le comportement
 * dans `information.configuration` ; ce modèle les met à plat pour un formulaire, et les fonctions
 * ci-dessous font l'aller-retour. Une couleur vide signifie « aucune valeur propre à l'enquête » :
 * le questionnaire retombe alors sur la valeur du thème.
 */
export interface SurveyStyleForm {
    logo: boolean;
    logoAlign: ALIGN_H;
    logoWidth: number | null;
    logoMargin: boolean;
    fontFamily: string;
    headerColor: string;
    backgroundColor: string;
    elementsColor: string;
    answersColor: string;
    legendColor: string;
    buttonColor: string;
    buttonIconColor: string;
    animate: boolean;
    animateStyle: ANIMATION_MODE;
}

/**
 * Le sélecteur de couleurs ramène toute valeur en minuscules et la réécrit dans le formulaire :
 * amorcer avec la même casse évite de prendre cette normalisation pour une modification, et donc
 * d'enregistrer le brouillon à la simple ouverture de la vue.
 */
const toPickerColor = (color?: string): string => color?.toLowerCase() ?? '';

/** Au-delà, un logo déborderait de la largeur utile du questionnaire. */
export const MAX_LOGO_WIDTH = 1232;

/**
 * Couleurs que le questionnaire applique quand l'enquête n'impose rien : ce sont elles que
 * rétablit la remise à zéro d'une couleur. En minuscules, comme le sélecteur de couleurs les
 * renvoie, pour qu'une remise à zéro ne passe pas pour une modification.
 */
export const DEFAULT_STYLE_COLORS = {
    header: '#05022f',
    background: '#ffffff',
    elements: '#000000',
    answers: '#2f54eb',
    legends: '#000000',
    buttons: '#2f54eb',
    buttonIcons: '#ffffff',
};

/** Le thème stocke la largeur du logo comme un nombre de pixels en toutes lettres, sans unité. */
const toPixelCount = (width?: string): number | null => {
    const pixels = Number.parseFloat(width ?? '');
    return Number.isFinite(pixels) ? pixels : null;
};

const toStoredWidth = (pixels: number | null): string | undefined => (pixels === null ? undefined : `${pixels}`);

export const buildSurveyStyleForm = (
    themeConfiguration: ThemeConfiguration,
    formConfiguration: FormConfiguration
): SurveyStyleForm => ({
    logo: themeConfiguration.logo ?? false,
    logoAlign: themeConfiguration.logoAlignH ?? ALIGN_H.CENTER,
    logoWidth: toPixelCount(themeConfiguration.logoWidth),
    logoMargin: themeConfiguration.displayMargin ?? false,
    fontFamily: themeConfiguration.globalFont?.value ?? '',
    headerColor: toPickerColor(themeConfiguration.globalHeaderColor),
    backgroundColor: toPickerColor(themeConfiguration.blockSurveyBackground),
    elementsColor: toPickerColor(themeConfiguration.globalFontColor),
    answersColor: toPickerColor(themeConfiguration.answerFontColor),
    legendColor: toPickerColor(themeConfiguration.legendFontColor),
    buttonColor: toPickerColor(themeConfiguration.buttonNavBackgroundColor),
    buttonIconColor: toPickerColor(themeConfiguration.buttonNavFontColor),
    animate: formConfiguration.animate ?? false,
    animateStyle: formConfiguration.animateStyle ?? ANIMATION_MODE.HORIZONTAL_SLIDE,
});

/** Les polices proposées viennent toutes du catalogue Google : `source` ne varie pas. */
export const toThemeConfiguration = (style: SurveyStyleForm): Partial<ThemeConfiguration> => ({
    logo: style.logo,
    logoAlignH: style.logoAlign,
    logoWidth: toStoredWidth(style.logoWidth),
    displayMargin: style.logoMargin,
    globalFont: style.fontFamily ? {source: 'google', value: style.fontFamily} : undefined,
    globalHeaderColor: style.headerColor || undefined,
    blockSurveyBackground: style.backgroundColor || undefined,
    globalFontColor: style.elementsColor || undefined,
    answerFontColor: style.answersColor || undefined,
    legendFontColor: style.legendColor || undefined,
    buttonNavBackgroundColor: style.buttonColor || undefined,
    buttonNavFontColor: style.buttonIconColor || undefined,
});

export const toFormConfiguration = (style: SurveyStyleForm): Partial<FormConfiguration> => ({
    animate: style.animate,
    animateStyle: style.animateStyle,
});
